/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description PatientEvent Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import patientEventService from '@nephro/services/patient-event-service';

// Constants
import {
  CREATE_PATIENT_EVENT,
  DELETE_PATIENT_EVENT_BY_ID,
  GET_ALL_PATIENT_EVENTS,
  GET_PATIENT_EVENT_BY_ID,
  UPDATE_PATIENT_EVENT,
  GET_ALL_EVENT_TYPES
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get patientEvents from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientEventsArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;
  try {
    const response = yield call(
      patientEventService.getPatientEvents,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get patientEvent info by id
 * @param { object } action - Action with payload data
 */
export function* getPatientEventById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { eventTypeId } = data;

  try {
    const response = yield call(
      patientEventService.getPatientEventById,
      eventTypeId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.patientEventNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create patientEvent
 * @param { object } action - Action with payload data
 */
export function* createPatientEvent(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const {
    description,
    event_type,
    patient,
    register_date,
    end_date,
    phone_number
  } = data;

  try {
    const response = yield call(
      patientEventService.createPatientEvent,
      description,
      event_type,
      patient,
      register_date,
      end_date,
      phone_number
    );
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update patientEvent
 * @param { object } action - Action with payload data
 */
export function* updatePatientEvent(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const {
    id_patient_event,
    description,
    event_type,
    patient,
    register_date,
    end_date,
    phone_number
  } = data;

  try {
    const response = yield call(
      patientEventService.updatePatientEvent,
      id_patient_event,
      description,
      event_type,
      patient,
      register_date,
      end_date,
      phone_number
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete patientEvent by id
 * @param { object } action - Action with payload data
 */
export function* deletePatientEventById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientEventService.deletePatientEventById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get Event Types from backend
 * @param { object } action - Action with payload data
 */
export function* getEventTypesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;
  try {
    const response = yield call(patientEventService.getEventTypes);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchPatientEventSaga() {
  yield takeLatest(GET_ALL_PATIENT_EVENTS, getPatientEventsArray);
  yield takeLatest(GET_PATIENT_EVENT_BY_ID, getPatientEventById);
  yield takeLatest(CREATE_PATIENT_EVENT, createPatientEvent);
  yield takeLatest(UPDATE_PATIENT_EVENT, updatePatientEvent);
  yield takeLatest(DELETE_PATIENT_EVENT_BY_ID, deletePatientEventById);
  yield takeLatest(GET_ALL_EVENT_TYPES, getEventTypesArray);
}
